import { useMemo, useState, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import {
  useWorkoutsSearch,
  DISCIPLINES,
  type ActualWorkout,
  type Discipline,
  type WorkoutsSearchFilters,
} from '@/lib/workouts';
import { disciplineEmoji } from '@/features/dashboard/RecentActivitiesCard';
import { disciplineLabel } from '@/features/dashboard/WorkoutCard';
import { formatRelativeTime, formatDateShort } from '@/lib/time';

/**
 * Vista LISTA del calendario `/entrenos`.
 *
 * Extraída de la antigua `WorkoutsListPage` (M2.13) — sólo el cuerpo
 * (filtros + tabla + paginación), sin el `<header>` superior ni el
 * `<section>` con el `<h1>` que ahora pinta el padre `WorkoutsPage`.
 *
 * El comportamiento es idéntico: filtros locales + `useWorkoutsSearch`,
 * paginación, sort clicable, stats agregados al pie.
 */

type SortKey = WorkoutsSearchFilters['sort'];
type SortOrder = WorkoutsSearchFilters['order'];

const DEFAULT_PAGE_SIZE = 20;

export function WorkoutsListView(): ReactElement {
  const [disciplinesSet, setDisciplinesSet] = useState<Set<Discipline>>(new Set());
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [minDistanceKm, setMinDistanceKm] = useState<string>('');
  const [minDurationMin, setMinDurationMin] = useState<string>('');
  const [source, setSource] = useState<'' | 'strava' | 'manual'>('');
  const [q, setQ] = useState<string>('');
  const [sort, setSort] = useState<SortKey>('date');
  const [order, setOrder] = useState<SortOrder>('desc');
  const [page, setPage] = useState<number>(1);
  const pageSize = DEFAULT_PAGE_SIZE;

  const filters: WorkoutsSearchFilters = useMemo(() => {
    const f: WorkoutsSearchFilters = {
      sort,
      order,
      page,
      pageSize,
    };
    if (from) f.fromISO = `${from}T00:00:00.000Z`;
    if (to) f.toISO = `${to}T23:59:59.999Z`;
    if (disciplinesSet.size > 0) f.disciplines = Array.from(disciplinesSet);
    const minDist = Number(minDistanceKm);
    if (Number.isFinite(minDist) && minDist > 0) f.minDistanceM = Math.round(minDist * 1000);
    const minDur = Number(minDurationMin);
    if (Number.isFinite(minDur) && minDur > 0) f.minDurationMin = Math.round(minDur);
    if (source) f.source = source;
    if (q.trim().length > 0) f.q = q.trim();
    return f;
  }, [from, to, disciplinesSet, minDistanceKm, minDurationMin, source, q, sort, order, page, pageSize]);

  const query = useWorkoutsSearch(filters);

  function toggleDiscipline(d: Discipline) {
    setDisciplinesSet((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });
    setPage(1);
  }

  function changeSort(key: SortKey) {
    if (sort === key) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setOrder(key === 'date' ? 'desc' : 'desc');
    }
    setPage(1);
  }

  function resetFilters() {
    setDisciplinesSet(new Set());
    setFrom('');
    setTo('');
    setMinDistanceKm('');
    setMinDurationMin('');
    setSource('');
    setQ('');
    setSort('date');
    setOrder('desc');
    setPage(1);
  }

  return (
    <div
      className="grid gap-4 lg:grid-cols-[260px_1fr]"
      data-testid="workouts-list-view"
    >
      <FiltersPanel
        disciplines={disciplinesSet}
        onToggleDiscipline={toggleDiscipline}
        from={from}
        to={to}
        onFromChange={(v) => {
          setFrom(v);
          setPage(1);
        }}
        onToChange={(v) => {
          setTo(v);
          setPage(1);
        }}
        minDistanceKm={minDistanceKm}
        onMinDistanceChange={(v) => {
          setMinDistanceKm(v);
          setPage(1);
        }}
        minDurationMin={minDurationMin}
        onMinDurationChange={(v) => {
          setMinDurationMin(v);
          setPage(1);
        }}
        source={source}
        onSourceChange={(v) => {
          setSource(v);
          setPage(1);
        }}
        q={q}
        onQChange={(v) => {
          setQ(v);
          setPage(1);
        }}
        onReset={resetFilters}
      />

      <div className="space-y-3">
        <ResultsTable
          data={query.data?.items ?? []}
          loading={query.isLoading}
          error={query.isError}
          sort={sort}
          order={order}
          onChangeSort={changeSort}
        />
        {query.data && (
          <FooterStats
            items={query.data.items}
            total={query.data.total}
            page={query.data.page}
            pageSize={query.data.pageSize}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel de filtros
// ---------------------------------------------------------------------------

function FiltersPanel({
  disciplines,
  onToggleDiscipline,
  from,
  to,
  onFromChange,
  onToChange,
  minDistanceKm,
  onMinDistanceChange,
  minDurationMin,
  onMinDurationChange,
  source,
  onSourceChange,
  q,
  onQChange,
  onReset,
}: {
  disciplines: Set<Discipline>;
  onToggleDiscipline: (d: Discipline) => void;
  from: string;
  to: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  minDistanceKm: string;
  onMinDistanceChange: (v: string) => void;
  minDurationMin: string;
  onMinDurationChange: (v: string) => void;
  source: '' | 'strava' | 'manual';
  onSourceChange: (v: '' | 'strava' | 'manual') => void;
  q: string;
  onQChange: (v: string) => void;
  onReset: () => void;
}): ReactElement {
  return (
    <aside
      aria-label="Filtros"
      className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Disciplina
        </h2>
        <ul className="mt-2 space-y-1">
          {DISCIPLINES.map((d) => (
            <li key={d}>
              <label className="flex items-center gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  checked={disciplines.has(d)}
                  onChange={() => onToggleDiscipline(d)}
                  className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-600"
                />
                <span aria-hidden="true">{disciplineEmoji(d)}</span>
                {disciplineLabel(d)}
              </label>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Rango de fechas
        </h2>
        <div className="mt-2 space-y-2">
          <label className="block text-xs text-slate-600">
            Desde
            <input
              type="date"
              value={from}
              onChange={(e) => onFromChange(e.target.value)}
              className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
            />
          </label>
          <label className="block text-xs text-slate-600">
            Hasta
            <input
              type="date"
              value={to}
              onChange={(e) => onToChange(e.target.value)}
              className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
            />
          </label>
        </div>
      </div>

      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mínimos</h2>
        <div className="mt-2 space-y-2">
          <label className="block text-xs text-slate-600">
            Distancia (km)
            <input
              type="number"
              min={0}
              step={0.5}
              value={minDistanceKm}
              onChange={(e) => onMinDistanceChange(e.target.value)}
              placeholder="0"
              className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
            />
          </label>
          <label className="block text-xs text-slate-600">
            Duración (min)
            <input
              type="number"
              min={0}
              step={5}
              value={minDurationMin}
              onChange={(e) => onMinDurationChange(e.target.value)}
              placeholder="0"
              className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
            />
          </label>
        </div>
      </div>

      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Origen</h2>
        <select
          value={source}
          onChange={(e) => onSourceChange(e.target.value as '' | 'strava' | 'manual')}
          className="mt-2 block w-full rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          <option value="">Cualquiera</option>
          <option value="strava">Strava</option>
          <option value="manual">Manual</option>
        </select>
      </div>

      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Búsqueda
        </h2>
        <input
          type="search"
          value={q}
          onChange={(e) => onQChange(e.target.value)}
          placeholder="device, gear, id…"
          className="mt-2 block w-full rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        />
      </div>

      <button
        type="button"
        onClick={onReset}
        className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
      >
        Limpiar filtros
      </button>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Tabla de resultados
// ---------------------------------------------------------------------------

interface ColSpec {
  key: SortKey;
  label: string;
  align?: 'right' | 'left';
  sortable?: boolean;
}

const COLUMNS: ColSpec[] = [
  { key: 'date', label: 'Fecha', sortable: true },
  { key: 'duration', label: 'Duración', sortable: true, align: 'right' },
  { key: 'distance', label: 'Distancia', sortable: true, align: 'right' },
  { key: 'tss', label: 'TSS', sortable: true, align: 'right' },
  { key: 'np', label: 'NP', sortable: true, align: 'right' },
  { key: 'hr', label: 'Avg HR', sortable: true, align: 'right' },
];

function ResultsTable({
  data,
  loading,
  error,
  sort,
  order,
  onChangeSort,
}: {
  data: ActualWorkout[];
  loading: boolean;
  error: boolean;
  sort: SortKey;
  order: SortOrder;
  onChangeSort: (key: SortKey) => void;
}): ReactElement {
  if (loading) {
    return (
      <p role="status" aria-live="polite" className="text-sm text-slate-600">
        Cargando entrenos…
      </p>
    );
  }
  if (error) {
    return (
      <p role="alert" className="text-sm text-rose-700">
        No se pudieron cargar los entrenos.
      </p>
    );
  }
  if (data.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
        Sin entrenos para estos filtros.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="w-full min-w-[720px] text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th scope="col" className="px-3 py-2">Tipo</th>
            {COLUMNS.map((col) => {
              const isActive = sort === col.key;
              const arrow = isActive ? (order === 'asc' ? '▲' : '▼') : '';
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={`px-3 py-2 ${col.align === 'right' ? 'text-right' : ''}`}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onChangeSort(col.key)}
                      aria-label={`Ordenar por ${col.label}`}
                      aria-sort={isActive ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
                      className="font-semibold text-slate-700 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
                    >
                      {col.label} <span aria-hidden="true">{arrow}</span>
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              );
            })}
            <th scope="col" className="px-3 py-2" />
          </tr>
        </thead>
        <tbody className="text-slate-800">
          {data.map((w) => (
            <WorkoutRow key={w.id} w={w} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WorkoutRow({ w }: { w: ActualWorkout }): ReactElement {
  const distKm = typeof w.distanceM === 'number' && w.distanceM > 0
    ? (w.distanceM / 1000)
    : null;
  const tss = w.tssComputed ?? w.tssEstimated ?? null;
  return (
    <tr className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
      <td className="px-3 py-2">
        <span className="text-lg" aria-hidden="true">
          {disciplineEmoji(w.discipline)}
        </span>{' '}
        <span className="text-xs text-slate-600">{disciplineLabel(w.discipline)}</span>
      </td>
      <td className="px-3 py-2">
        <div className="text-sm font-medium text-slate-900">{formatDateShort(w.datetime)}</div>
        <div className="text-xs text-slate-500">{formatRelativeTime(w.datetime)}</div>
      </td>
      <td className="px-3 py-2 text-right">{Math.round(w.durationMin)} min</td>
      <td className="px-3 py-2 text-right">{distKm !== null ? `${distKm.toFixed(2)} km` : '—'}</td>
      <td className="px-3 py-2 text-right">{tss !== null ? Math.round(tss) : '—'}</td>
      <td className="px-3 py-2 text-right">{w.normalizedPower ?? '—'}</td>
      <td className="px-3 py-2 text-right">{w.avgHr ?? '—'}</td>
      <td className="px-3 py-2">
        <Link
          to="/entreno/$id"
          params={{ id: String(w.id) }}
          className="text-xs font-medium text-teal-700 hover:text-teal-900 hover:underline focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
          aria-label={`Ver detalle del entreno del ${formatDateShort(w.datetime)}`}
        >
          Ver →
        </Link>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Footer con stats agregados + paginación
// ---------------------------------------------------------------------------

function FooterStats({
  items,
  total,
  page,
  pageSize,
  onPrev,
  onNext,
}: {
  items: ActualWorkout[];
  total: number;
  page: number;
  pageSize: number;
  onPrev: () => void;
  onNext: () => void;
}): ReactElement {
  let totalMin = 0;
  let totalDist = 0;
  let totalTss = 0;
  for (const w of items) {
    totalMin += w.durationMin;
    totalDist += w.distanceM ?? 0;
    totalTss += w.tssComputed ?? w.tssEstimated ?? 0;
  }
  const hours = totalMin / 60;
  const km = totalDist / 1000;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <footer className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm shadow-sm">
      <div className="text-xs text-slate-600">
        <strong className="text-slate-900">{total}</strong> entrenos · página{' '}
        <strong className="text-slate-900">{page}</strong> de{' '}
        <strong className="text-slate-900">{totalPages}</strong> · esta página:{' '}
        {hours.toFixed(1)} h · {km.toFixed(1)} km · {Math.round(totalTss)} TSS
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={!canPrev}
          className="rounded border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          ← Anterior
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canNext}
          className="rounded border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
        >
          Siguiente →
        </button>
      </div>
    </footer>
  );
}
